/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import * as openEnums from "../../types/enums.js";
import { OpenEnum } from "../../types/enums.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";

export const AuthzChallengeOperation = {
  Require: "require",
  RequireAny: "require_any",
  Filter: "filter",
} as const;
export type AuthzChallengeOperation = OpenEnum<typeof AuthzChallengeOperation>;

export const AuthzChallengeOutcome = {
  Allow: "allow",
  Deny: "deny",
  Error: "error",
} as const;
export type AuthzChallengeOutcome = OpenEnum<typeof AuthzChallengeOutcome>;

/**
 * Kind of principal.
 */
export const AuthzChallengePrincipalType = {
  User: "user",
  ApiKey: "api_key",
  Assistant: "assistant",
  Agent: "agent",
  Workload: "workload",
} as const;
/**
 * Kind of principal.
 */
export type AuthzChallengePrincipalType = OpenEnum<
  typeof AuthzChallengePrincipalType
>;

export const AuthzChallengeReason = {
  GrantMatched: "grant_matched",
  NoGrants: "no_grants",
  ScopeUnsatisfied: "scope_unsatisfied",
  DenyGrant: "deny_grant",
  InvalidCheck: "invalid_check",
  RbacSkippedApikey: "rbac_skipped_apikey",
  DevOverride: "dev_override",
} as const;
export type AuthzChallengeReason = OpenEnum<typeof AuthzChallengeReason>;

/**
 * How the challenge was resolved.
 */
export const AuthzChallengeResolutionType = {
  RoleAssigned: "role_assigned",
  Dismissed: "dismissed",
} as const;
/**
 * How the challenge was resolved.
 */
export type AuthzChallengeResolutionType = OpenEnum<
  typeof AuthzChallengeResolutionType
>;

export type AuthzChallenge = {
  /**
   * Total grants evaluated.
   */
  evaluatedGrantCount: number;
  /**
   * Unique challenge identifier.
   */
  id: string;
  /**
   * Number of grants that matched.
   */
  matchedGrantCount: number;
  operation: AuthzChallengeOperation;
  /**
   * Organization the principal was acting in.
   */
  organizationId: string;
  outcome: AuthzChallengeOutcome;
  /**
   * User avatar URL when available.
   */
  photoUrl?: string | undefined;
  /**
   * Kind of principal.
   */
  principalType: AuthzChallengePrincipalType;
  /**
   * Principal URN e.g. user:<uuid> or api_key:<id>.
   */
  principalUrn: string;
  /**
   * Project scope (empty for org-level checks).
   */
  projectId?: string | undefined;
  reason: AuthzChallengeReason;
  /**
   * Role slug assigned (when resolution_type=role_assigned).
   */
  resolutionRoleSlug?: string | undefined;
  /**
   * How the challenge was resolved.
   */
  resolutionType?: AuthzChallengeResolutionType | undefined;
  /**
   * When the challenge was resolved by an admin.
   */
  resolvedAt?: Date | undefined;
  /**
   * URN of the admin who resolved.
   */
  resolvedBy?: string | undefined;
  /**
   * Resource ID of the check.
   */
  resourceId?: string | undefined;
  /**
   * Resource kind of the check.
   */
  resourceKind?: string | undefined;
  /**
   * Roles the principal had loaded.
   */
  roleSlugs: Array<string>;
  /**
   * Scope that was checked.
   */
  scope: string;
  /**
   * Complete selector captured for the check. Omitted for legacy or malformed challenge data.
   */
  selector?: { [k: string]: string } | undefined;
  /**
   * When the authz decision was made.
   */
  timestamp: Date;
  /**
   * Email when available.
   */
  userEmail?: string | undefined;
};

/** @internal */
export const AuthzChallengeOperation$inboundSchema: z.ZodMiniType<
  AuthzChallengeOperation,
  unknown
> = openEnums.inboundSchema(AuthzChallengeOperation);

/** @internal */
export const AuthzChallengeOutcome$inboundSchema: z.ZodMiniType<
  AuthzChallengeOutcome,
  unknown
> = openEnums.inboundSchema(AuthzChallengeOutcome);

/** @internal */
export const AuthzChallengePrincipalType$inboundSchema: z.ZodMiniType<
  AuthzChallengePrincipalType,
  unknown
> = openEnums.inboundSchema(AuthzChallengePrincipalType);

/** @internal */
export const AuthzChallengeReason$inboundSchema: z.ZodMiniType<
  AuthzChallengeReason,
  unknown
> = openEnums.inboundSchema(AuthzChallengeReason);

/** @internal */
export const AuthzChallengeResolutionType$inboundSchema: z.ZodMiniType<
  AuthzChallengeResolutionType,
  unknown
> = openEnums.inboundSchema(AuthzChallengeResolutionType);

/** @internal */
export const AuthzChallenge$inboundSchema: z.ZodMiniType<
  AuthzChallenge,
  unknown
> = z.pipe(
  z.object({
    evaluated_grant_count: z.int(),
    id: z.string(),
    matched_grant_count: z.int(),
    operation: AuthzChallengeOperation$inboundSchema,
    organization_id: z.string(),
    outcome: AuthzChallengeOutcome$inboundSchema,
    photo_url: z.optional(z.string()),
    principal_type: AuthzChallengePrincipalType$inboundSchema,
    principal_urn: z.string(),
    project_id: z.optional(z.string()),
    reason: AuthzChallengeReason$inboundSchema,
    resolution_role_slug: z.optional(z.string()),
    resolution_type: z.optional(AuthzChallengeResolutionType$inboundSchema),
    resolved_at: z.optional(
      z.pipe(z.iso.datetime({ offset: true }), z.transform(v => new Date(v))),
    ),
    resolved_by: z.optional(z.string()),
    resource_id: z.optional(z.string()),
    resource_kind: z.optional(z.string()),
    role_slugs: z.array(z.string()),
    scope: z.string(),
    selector: z.optional(z.record(z.string(), z.string())),
    timestamp: z.pipe(
      z.iso.datetime({ offset: true }),
      z.transform(v => new Date(v)),
    ),
    user_email: z.optional(z.string()),
  }),
  z.transform((v) => {
    return remap$(v, {
      "evaluated_grant_count": "evaluatedGrantCount",
      "matched_grant_count": "matchedGrantCount",
      "organization_id": "organizationId",
      "photo_url": "photoUrl",
      "principal_type": "principalType",
      "principal_urn": "principalUrn",
      "project_id": "projectId",
      "resolution_role_slug": "resolutionRoleSlug",
      "resolution_type": "resolutionType",
      "resolved_at": "resolvedAt",
      "resolved_by": "resolvedBy",
      "resource_id": "resourceId",
      "resource_kind": "resourceKind",
      "role_slugs": "roleSlugs",
      "user_email": "userEmail",
    });
  }),
);

export function authzChallengeFromJSON(
  jsonString: string,
): SafeParseResult<AuthzChallenge, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => AuthzChallenge$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'AuthzChallenge' from JSON`,
  );
}
